import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {TscThemeName, TscThemeNames, TscStyles, ControlButton, Glyphs, TscButtonType, Spinner} from '@techsmith/tsc-cloud-style-guide';
import cssConstants from '../../constants/cssConstants';
import MediaTabsHeader from './MediaTabsHeader';
import {ITab} from '../../model/tab';
import config from '../../service/config';
import {IWorkerState} from '../../model/workerState';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import TranscriptTab from '../transcript/TranscriptTab';
import Conversation from '../conversation/Conversation';
import {IComment} from '../../model/commentModel';
import {useTranslation} from 'react-i18next';
import {conversationsStore} from '../../context/conversationsProvider';
import SettingsTab from './SettingsTab';
import EditMediaTab from './edit-media/EditMediaTab';
import {IConversationData} from '../../model/conversationModel';
import {ITranscript} from '../../model/captionModels';
import {transcriptStore} from '../../context/transcriptProvider';
import DetailsTab from './details/DetailsTab';
import {selectedConversationStore} from '../../context/selectedConversationProvider';
import tabNames from '../../constants/tabNames';
import {mediaTabsStore, updateMediaTabsState} from '../../context/mediaTabsProvider';

const MediaTabsWrapper = styled.div<{theme: TscThemeNames}>`
   background-color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   display: flex;
   flex-direction: column;
   flex: 0 0 auto;
   height: inherit;
   z-index: ${cssConstants.zIndex.conversation};
   outline: none;
   position: relative;
   overflow: hidden;
   border-radius: ${TscStyles.border.radius.xl};
   width: 21.75rem;

   @media screen and (max-width: ${cssConstants.mediaTabsDisplayBreakpoint}px)  {
      border-radius: 0;
      width: 100%;
      max-width: 100%;
      max-height: 60vh;
   };
`;

const CloseButton = styled.span`
   padding: .25rem;
   margin: 0;
`;

const BaseTabsWrapper = styled.div`
   width: 100%;
   display: flex;
   margin: 0 auto;
   box-sizing: border-box;
`;

export const TabsContentWrapper = styled(BaseTabsWrapper)`
   flex-direction: column;
   justify-content: center;
   align-items: center;
   max-width: 21.875rem;
   z-index: ${cssConstants.zIndex.viewPageElement};
`;

const TabsBarWrapper = styled(BaseTabsWrapper)<{theme: TscThemeNames}>`
   position: sticky;
   top: 0;
   border-radius: ${TscStyles.border.radius.xl} ${TscStyles.border.radius.xl} 0 0;
   background: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.medium : TscStyles.color.ui.dawn.light};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   border-bottom: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark : TscStyles.color.ui.dawn.medium};

   @media screen and (max-width: ${cssConstants.mediaTabsDisplayBreakpoint}px)  {
      border-radius: 0;
      margin: 0;
      width: 100%;
   }
`;

const Card = styled.div<{theme: TscThemeNames}>`
   position: relative;
   max-width: 21.875rem;
   width: 100%;
   border-radius: 0 0 ${TscStyles.border.radius.xl} ${TscStyles.border.radius.xl};
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   overflow: auto;
   height: 100%;
   
   @media screen and (max-width: ${cssConstants.mediaTabsDisplayBreakpoint}px)  {
      border-radius: 0;
      width: 100%;
      max-width: 100%;
   };
`;

const TabSpinner = styled(Spinner)`
   display: flex;
   justify-content: center;
   align-items: center;
   height: 100%;
`;

export const MediaTabsBase: React.FC<IMediaTabsProps & IStateMappedProps> = ({canClosePanel, conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode, currentTab, IsReady, conversations, VideoReviewId, transcript}) => {
   // The following variables are intentionally referenced to ensure context/HOC updates work correctly.
   // Removing them causes a bug where the tabs do not show, even though they appear unused.
   void IsReady;

   const {t} = useTranslation();
   const theme = useTscTheme();
   const [tabs, setTabs] = useState<ITab[]>([]);
   const [hasConversation, setHasConversation] = React.useState(null);
   const [hasTranscript, setHasTranscript] = React.useState(null);
   const [includeDetailsTab, setIncludeDetailsTab] = React.useState(window.innerWidth <= cssConstants.mediaTabsDisplayBreakpoint);
   const isInitialRender = useRef(true);

   const updateSelectedTab = (tabName: string) => {
      updateMediaTabsState({currentTab: tabName});
      setTabs(prevTabs =>
         prevTabs.map(tab2 => ({
            ...tab2,
            isSelected: tab2.text === tabName
         }))
      );
   };

   useEffect(() => {
      // don't run the first time the page loads, it will be handled by the useEffect triggered by the transcript/conversations loading
      if (isInitialRender.current) {
         isInitialRender.current = false;
         return;
      }

      if (includeDetailsTab) {
         const newTabs = [...tabs];
         if (!newTabs.some(mediaTab => mediaTab.text === tabNames.details)) {
            newTabs.unshift({
               text: tabNames.details,
               isSelected: currentTab === tabNames.details,
               onClick: () => updateSelectedTab(tabNames.details),
               href: ''
            });
         }
         setTabs(newTabs);
      } else {
         const newTabs = [...tabs];
         if (newTabs[0]?.text === tabNames.details) {
            newTabs.shift();
            if (newTabs.length > 0) {
               if (currentTab === tabNames.details) {
                  newTabs.forEach(tab2 => (tab2.isSelected = false));
                  newTabs[0].isSelected = true;
                  updateMediaTabsState({currentTab: newTabs[0]?.text});
               }
            }
         }
         setTabs(newTabs);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [includeDetailsTab]);

   const handleResize = () => {
      setIncludeDetailsTab(window.innerWidth <= cssConstants.mediaTabsDisplayBreakpoint);
   };

   useEffect(() => {
      window.addEventListener('resize', handleResize);

      // Cleanup event listener on component unmount
      return () => {
         window.removeEventListener('resize', handleResize);
      };
   }, []);

   useEffect(() => {
      // don't populate tabs until we know if the media has a conversations/transcripts
      if (hasConversation === null || hasTranscript === null) {
         return;
      }
      const newTabs: ITab[] = [];
      if (includeDetailsTab) {
         newTabs.push({
            text: tabNames.details,
            isSelected: currentTab === tabNames.details,
            onClick: () => updateSelectedTab(tabNames.details),
            href: ''
         });
      }
      const includeCommentsTab = config.watchConfig.isOwnerAdminOrTeamEditor || hasConversation;
      if (includeCommentsTab) {
         newTabs.push({
            text: tabNames.comments,
            isSelected: currentTab === tabNames.comments,
            onClick: () => updateSelectedTab(tabNames.comments),
            href: ''
         });
      }
      if (hasTranscript) {
         newTabs.push({
            text: tabNames.transcript,
            isSelected: currentTab === tabNames.transcript,
            onClick: () => updateSelectedTab(tabNames.transcript),
            href: ''
         });
      }
      if (config.watchConfig.isOwnerAdminOrTeamEditor) {
         newTabs.push({
            text: tabNames.settings,
            isSelected: currentTab === tabNames.settings,
            onClick: () => updateSelectedTab(tabNames.settings),
            href: ''
         });
         if (!config.featureSwitches.IsScreencast) {
            newTabs.push({
               text: tabNames.editMedia,
               isSelected: currentTab === tabNames.editMedia,
               onClick: () => updateSelectedTab(tabNames.editMedia),
               href: ''
            });
         }
      }
      if (newTabs.length > 0 && !newTabs.some(tab2 => tab2.text === currentTab)) {
         newTabs[0].isSelected = true;
         updateMediaTabsState({currentTab: newTabs[0].text});
      }
      setTabs(newTabs);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [hasTranscript, hasConversation]);

   useEffect(() => {
      if (config.featureSwitches.UseSingleConversation) {
         if (conversations) {
            setHasConversation(conversations?.length > 0);
         }
      } else {
         setHasConversation(config.watchConfig.isOwnerAdminOrTeamEditor || (VideoReviewId && conversations?.length > 0));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [VideoReviewId, conversations?.length]);

   useEffect(() => {
      setHasTranscript(!!transcript);
   }, [transcript]);

   return (
      <MediaTabsWrapper theme={theme}>
         <TabsBarWrapper theme={theme}>
            <MediaTabsHeader tabs={tabs} />
            {canClosePanel && (
               <CloseButton>
                  <ControlButton
                     glyph={<Glyphs.Close16x16 />}
                     buttonType={TscButtonType.tertiary}
                     themeName={theme}
                     title={t('general.close')}
                     testId="hide-media-tabs-button"
                     aria={{'aria-label': t('general.close')}}
                     onClick={() => updateMediaTabsState({mediaTabsPanelOpen: false, hasToggledMediaTabs: true})}
                  />
               </CloseButton>
            )}
         </TabsBarWrapper>
         <Card theme={theme}>
            {!currentTab && (
               <TabSpinner themeName={theme} />
            ) }
            {currentTab === tabNames.details && <DetailsTab />}
            {currentTab === tabNames.comments && (
               <Conversation
                  initialSelectedComment={initialSelectedComment}
                  conversationLoadingState={conversationLoadingState}
                  setConversationLoadingState={setConversationLoadingState}
                  smartPlayerDisplayMode={smartPlayerDisplayMode}
               />
            )}
            {currentTab === tabNames.transcript && <TranscriptTab />}
            {currentTab === tabNames.settings && <SettingsTab />}
            {currentTab === tabNames.editMedia && <EditMediaTab />}
         </Card>
      </MediaTabsWrapper>
   );
};

export interface IMediaTabsProps {
   canClosePanel: boolean;
   conversationLoadingState: IWorkerState;
   setConversationLoadingState: (updatedState: IWorkerState) => void;
   initialSelectedComment: IComment;
   smartPlayerDisplayMode: string;
}

export interface IStateMappedProps {
   currentTab: string;
   IsReady: boolean;
   conversations: IConversationData[];
   VideoReviewId: string;
   transcript: ITranscript;
}

const mapStatesToProps = (
   {canClosePanel, conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode}: IMediaTabsProps,
   {currentTab}: Partial<IStateMappedProps>,
   {IsReady}: Partial<IStateMappedProps>,
   {conversations}: Partial<IStateMappedProps>,
   {VideoReviewId}: Partial<IStateMappedProps>,
   {transcript}: Partial<IStateMappedProps>
): IMediaTabsProps & IStateMappedProps => ({canClosePanel, conversationLoadingState, setConversationLoadingState, initialSelectedComment, smartPlayerDisplayMode, currentTab, IsReady, conversations, VideoReviewId, transcript});

export default withMemoizedContexts(mapStatesToProps, mediaTabsStore, mediaStore, conversationsStore, selectedConversationStore, transcriptStore)(MediaTabsBase);
